import type { Island } from "./types";

/**
 * The five raids. Multisets come from the kept design tool
 * (scripts/tune-levels.ts — seeded search where every candidate's optimum is
 * proven by a real HiGHS solve): island 1 is all flat tops and friendly (the
 * deterministic greedy grab equals the optimum), islands 2–5 are designed
 * traps where the BEST of four greedy baselines (value-first, density-first,
 * fragile-up-top, bulk-first — all gravity lowest-fit) provably falls short —
 * the obvious play earns two stars, never three.
 *
 * The decks are small and grow 12 → 18 → 24 → 36 → 48 cells; loot is grouped by
 * type on the quay, so a lean shelf reads cleanly. The two tall raids (islands
 * 4–5) are non-stackable-heavy with only a little wood: the cheap planks are the
 * only platform that lifts cargo into the upper layers, so the optimum is
 * *forced* to load the wood (proven in levels.test by re-solving without it).
 * Every `opt` is re-proven with real HiGHS and every `greedy` re-derived in
 * levels.test.ts on every test run.
 */
export const ISLANDS: Island[] = [
  {
    // tune-levels seed 0 — all flat tops: teaches stacking + sacrifice
    name: "Sandflat Cay",
    flavor: "a sleepy trading post — even the gulls are unarmed",
    cols: 3, rows: 2, maxH: 2,
    loot: ["sugar", "sugar", "muskets", "muskets", "planks", "silk"],
    opt: 99,
    greedy: 99,
  },
  {
    // tune-levels seed 2 — the first domed lids: nothing stacks on a chest
    name: "Smuggler's Hollow",
    flavor: "contraband under tarps — mind what you stack on what",
    cols: 3, rows: 3, maxH: 2,
    loot: ["rum", "muskets", "muskets", "muskets", "chest", "powder", "planks", "cannon", "silk"],
    opt: 206,
    greedy: 178,
  },
  {
    // tune-levels seed 2 — idols and silk: the good stuff is awkward
    name: "Parrot Atoll",
    flavor: "golden idols, jealous parrots, and silk worth more than both",
    cols: 4, rows: 3, maxH: 2,
    loot: ["rum", "muskets", "muskets", "muskets", "chest", "powder", "planks", "planks", "silk", "silk", "idol", "cannon"],
    opt: 284,
    greedy: 256,
  },
  {
    // hand-designed, tool-verified: a tall pier with FOUR stackable types
    // (planks, silk, muskets, tea chests) competing as platforms — the puzzle is
    // which goods to spend as a shelf, not "use all the wood". The proven
    // optimum uses all four as platforms; the value-first grab falls 29% short.
    name: "Fort Mordant",
    flavor: "the garrison's armoury — pick your shelves, not just the shiny iron",
    cols: 4, rows: 3, maxH: 3,
    loot: ["cannon", "cannon", "chest", "chest", "idol", "idol", "planks", "silk", "muskets", "tea", "cage"],
    opt: 349,
    greedy: 248,
  },
  {
    // hand-designed, tool-verified — the grand finale: still four stackable
    // types, wider and taller; the obvious value-first play falls 32% short.
    name: "The Sunken Crown",
    flavor: "the legend itself — everything glitters; the clever stack wins",
    cols: 4, rows: 4, maxH: 3,
    loot: ["cannon", "cannon", "cannon", "chest", "chest", "chest", "idol", "idol", "planks", "planks", "silk", "silk", "muskets", "tea", "cage"],
    opt: 470,
    greedy: 318,
  },
];
